import type { IProductBundleOption } from '@rogue/universal'
import { HYBRID_RACK_LEVEL_OPTION_TITLES, REAR_CROSSMEMBER_OPTION_TITLE } from './hybridConstants'
import type { BuyRequest, IRogue, Product } from '@/js/services/RogueTypes'

// Color, height, depth and width are all attributes of the simple the bundle's Rig configurable resolves to - none of them is a bundle option of its own. Walked through rogue.getProductById rather than getSimpleForBuyRequest to keep the cart-side builders callable with a plain rogue instead of a live pinia.
export function getHybridBundleSimple(rogue: IRogue, bundleBuyRequest: BuyRequest | undefined): Product | undefined {
  let node = bundleBuyRequest

  while (node?.children?.length) {
    node = node.children[0]
  }

  return node ? rogue.getProductById(node.productId) : undefined
}

export function getHybridWidth(rogue: IRogue, bundleBuyRequest: BuyRequest | undefined): string | undefined {
  const width = getHybridBundleSimple(rogue, bundleBuyRequest)?.attributes?.mass_width

  return width ? String(width) : undefined
}

// One label for every surface that names a unit - PDP accordion, cart section, invoice section - so they cannot drift apart again. One interpolated key per shape, not concatenated fragments, since translators need the whole phrase.
export function getHybridUnitLabel(rogue: IRogue, bundleBuyRequest: BuyRequest | undefined, index: number): string {
  const width = getHybridWidth(rogue, bundleBuyRequest)

  return width
    ? rogue.__('Unit {index} - {width} Width', { index: index + 1, width })
    : rogue.__('Unit {index}', { index: index + 1 })
}

// Bare inches ('100', '108'), unlike getHybridWidth, which keeps the inch mark its callers match on. Height is the shared rack's, so every rig on a group reports the same one.
export function getHybridHeight(rogue: IRogue, bundleBuyRequest: BuyRequest | undefined): string | undefined {
  const height = getHybridBundleSimple(rogue, bundleBuyRequest)?.attributes?.rack_height

  return height ? String(height).replace(/\D/g, '') : undefined
}

// The rack/rig selector is declared by the catalog's addonType, never by its customer-facing label ('Rig' on HYBRID-RIG-BUNDLE, 'Rack:' before it) - and any future per-rig option is likely to be named 'Rig something', which a title match would wrongly pull out of the accordion.
export function isHybridRackLevelOption(option: Pick<IProductBundleOption, 'title' | 'addonType'> | undefined): boolean {
  if (!option) return false
  if (option.addonType === 'main') return true

  const title = String(option.title ?? '').toLowerCase()

  return HYBRID_RACK_LEVEL_OPTION_TITLES.some(rackTitle => title.includes(rackTitle))
}

function findRearCrossmember(rogue: IRogue, bundle: BuyRequest | undefined): BuyRequest | undefined {
  if (!bundle) return undefined

  const bundleOptions = rogue.getProductById(bundle.productId)?.bundleOptions ?? []

  return (bundle.children ?? []).find(child =>
    String(bundleOptions.find(opt => opt.id === child.metadata?.bundleOptionId)?.title ?? '').toLowerCase().includes(REAR_CROSSMEMBER_OPTION_TITLE))
}

// A new rig is cloned from the catalog template, whose default nameplate ignores the rack color already chosen, and bundle-option's nameplate sync deliberately skips its first run - so the clone inherits Rig 1's selection instead. Its own metadata (bundleOptionId) is kept.
export function inheritRearCrossmember(rogue: IRogue, rackBuyRequest: BuyRequest | undefined, newUnit: BuyRequest): void {
  const source = findRearCrossmember(rogue, rackBuyRequest)
  const target = findRearCrossmember(rogue, newUnit)

  if (!source || !target) return

  const copy: BuyRequest = JSON.parse(JSON.stringify(source))
  target.productId = copy.productId
  target.qty = copy.qty
  target.children = copy.children
}
